import React from "react";
import { observer } from "mobx-react";
import toast from "react-hot-toast";
import {
  Button,
  Checkbox,
  Divider,
  HStack,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { translations } from "@/constants/translations";
import { useStore } from "@/store";
import { getOrgActivities } from "@/store/types/register";
import { api } from "@/api";
import { renderInnerHtml } from "@/helpers/dom";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { AppLink } from "@/routing/AppLink";
import { H2, H3 } from "@/components/Heading";
import { FormSummaryItem } from "@/components/Form/FormSummaryItem";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { getApiErrorMessage } from "@/api/getApiErrorMessage";
import { telemetry } from "@/telemetry";

export const RegisterReviewPage = observer(() => {
  const {
    activeLanguage,
    registerData,
    setRegisterData,
    setOrderData,
    orderData,
  } = useStore();
  const language = translations.pages["account-registration"][activeLanguage];

  const navigate = useAppNavigate();

  const apiCreate = useApiMutate({
    mutationFn: api.createRegistration,
    onSuccess: () => {
      telemetry.trackEvent("registration_submitted", { result: "ok" });
      setOrderData({
        ...orderData,
        emailAddress: registerData.billingContactEmail,
        confirmEmailAddress: registerData.confirmEmail,
        phoneNumber: registerData.billingContactPhoneNumber,
      });
      navigate({
        to: "/register/confirm",
      });
    },
    onError: (data) => {
      telemetry.trackEvent("registration_submitted", {
        result: "error",
        status: data.response?.status,
      });
      toast.error(getApiErrorMessage(data));
    },
  });

  const handleConfirm = () =>
    apiCreate.mutate({
      ...registerData,
      organizationActivities: getOrgActivities(registerData),
    });

  const handleCheckboxConfirm = (value: boolean) => {
    setRegisterData({
      ...registerData,
      isDetailsConfirmed: value,
    });
  };

  const handleCheckboxEmailUpdates = (value: boolean) => {
    setRegisterData({
      ...registerData,
      subscribeToEmailUpdates: value,
    });
  };

  const editLink = (
    <AppLink to="/register/form">{renderInnerHtml(language.edit)}</AppLink>
  );

  return (
    <Stack spacing="14">
      <H2 textAlign="center">
        {renderInnerHtml(language.please_review_you_registration)}
      </H2>

      {/* Organization contact informion */}
      <Stack spacing="8">
        <HStack justifyContent="space-between">
          <H3>{renderInnerHtml(language.organization_contact)}</H3>
          {editLink}
        </HStack>
        <SimpleGrid
          columns={{ md: 2, sm: 1 }}
          spacingX="8"
          spacingY="4"
        >
          <FormSummaryItem
            label={language.organizations_legal_name}
            value={registerData.organizationLegalName}
          />
          <FormSummaryItem
            label={language.operating_name}
            value={registerData.operatingName}
          />
          <FormSummaryItem
            label={language.street_address}
            value={registerData.streetAddress}
          />
          <FormSummaryItem
            label={language.unit_suit}
            value={registerData.unitSuite}
          />
          <FormSummaryItem
            label={language.city}
            value={registerData.city}
          />

          <FormSummaryItem
            label={language.province}
            value={registerData.provinceTerritory}
          />

          <FormSummaryItem
            label={language.postal_code}
            value={registerData.postalCode}
          />
        </SimpleGrid>
      </Stack>

      {/* Billing contact information: */}
      <Stack spacing="8">
        <HStack justifyContent="space-between">
          <H3>{renderInnerHtml(language.billing_contact_info)}</H3>
          {editLink}
        </HStack>
        <SimpleGrid
          columns={{ sm: 1, md: 2 }}
          spacingX="8"
          spacingY="4"
        >
          <FormSummaryItem
            label={language.billing_name}
            value={registerData.billingContactName}
          />
          <FormSummaryItem
            label={language.billing_title}
            value={registerData.billingContactTitle}
          />
          <FormSummaryItem
            label={language.billing_phone_number}
            value={registerData.billingContactPhoneNumber}
          />
          <FormSummaryItem
            label={language.billing_email_address}
            value={registerData.billingContactEmail}
          />
        </SimpleGrid>
      </Stack>

      {/* Organization type and ownership: */}
      <Stack spacing="8">
        <HStack justifyContent="space-between">
          <H3>{renderInnerHtml(language.organization_type_ownership)}</H3>
          {editLink}
        </HStack>
        <SimpleGrid
          columns={{ sm: 1, md: 2 }}
          alignItems="flex-start"
          spacingX="8"
          spacingY="4"
        >
          <Stack spacing="4">
            <FormSummaryItem
              label={language.organization_type}
              value={registerData.organizationTypes.join(", ")}
            />
            <FormSummaryItem
              label={language.tax_id}
              value={registerData.taxID}
            />

            <FormSummaryItem
              label={language.location_of_incorporation}
              value={registerData.locationOfIncorporation}
            />
          </Stack>
          <Stack spacing="6">
            <Stack>
              <Text fontWeight="bold">
                {renderInnerHtml(language.suits_liens_judgements)}
              </Text>
              <Text>{renderInnerHtml(language.are_there_suits)}</Text>
              <Text>{registerData.hasSuitsLiensJudgements ? "Yes" : "No"}</Text>
            </Stack>
            <Stack>
              <Text fontWeight="bold">{language.if_yes_description}</Text>
              <Text>{registerData.suitsLiensJudgementsDescription}</Text>
            </Stack>
          </Stack>
        </SimpleGrid>
      </Stack>

      {/* Organization ownership */}
      <Stack spacing="8">
        <HStack justifyContent="space-between">
          <H3> {renderInnerHtml(language.organization_ownership)}</H3>
          {editLink}
        </HStack>
        <SimpleGrid
          columns={{ sm: 1, md: 2 }}
          spacingX="8"
          spacingY="4"
        >
          <FormSummaryItem
            label={language.owner_name}
            value={registerData.organizationOwnershipName}
          />
          <FormSummaryItem
            label={language.owner_title}
            value={registerData.organizationOwnershipTitle}
          />
          <FormSummaryItem
            label={language.owner_ownership}
            value={registerData.organizationOwnershipPercentage}
          />
          <FormSummaryItem
            label={language.home_address}
            value={registerData.organizationOwnershipHomeAddress}
          />
        </SimpleGrid>
      </Stack>

      {/* Organization's activities: */}
      <Stack spacing="8">
        <HStack justifyContent="space-between">
          <H3>{renderInnerHtml(language.organizations_activities)}</H3>
          {editLink}
        </HStack>
        <Stack>
          <FormSummaryItem
            label={language.type_of_business}
            value={getOrgActivities(registerData).join(", ")}
          />
          <FormSummaryItem
            label={language.is_the_purchasing}
            value={registerData.isBuyingOnBehalfOfThirdParty ? "Yes" : "No"}
          />
        </Stack>
      </Stack>

      <Divider />

      <Checkbox
        id="confirmRegistration"
        isChecked={registerData.isDetailsConfirmed}
        onChange={() => handleCheckboxConfirm(!registerData.isDetailsConfirmed)}
      >
        {renderInnerHtml(language.i_confirm_registration_details)}
      </Checkbox>

      <Stack>
        <Text fontWeight="bold">{renderInnerHtml(language.optional)}</Text>
        <Checkbox
          id="receiveEmail"
          isChecked={registerData.subscribeToEmailUpdates}
          onChange={() =>
            handleCheckboxEmailUpdates(!registerData.subscribeToEmailUpdates)
          }
        >
          {renderInnerHtml(language.i_like_emails)}
        </Checkbox>
      </Stack>

      <HStack justifyContent="flex-end">
        <Button
          onClick={handleConfirm}
          isDisabled={!registerData.isDetailsConfirmed || apiCreate.isPending}
        >
          {renderInnerHtml(language.submit_registration)}
        </Button>
      </HStack>
    </Stack>
  );
});
